import React, {useEffect, useState, Fragment, useRef} from 'react';
import {Alert, Button, FormControl, Spinner, Tab, Tabs, Card} from "react-bootstrap";
import {useDispatch, useSelector} from "react-redux";
import {Link, useNavigate, useParams} from "react-router-dom";
import * as t from "counterpart";
import {Helmet} from "react-helmet";
import Datepicker from "react-datepicker";
import {Plugins} from "@capacitor/core";
import moment from "moment/moment";
import ProductsCarousel from "./Blocks/ProductsCarousel";
import ProductBox from "./Blocks/ProductBox";
import {addToCart} from "../Cart/actions";
import {addToCompare} from "./actions";
import api from "../../utilities/API";

// image gallery
import ImageGallery from "yet-another-react-lightbox";
import { Inline, Thumbnails, Fullscreen, Video } from "yet-another-react-lightbox/plugins";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import PaymentHooks from "./PaymentHooks";

import "react-datepicker/dist/react-datepicker.css";
import "./style/Product.scss";
import ProductBadges from "./Blocks/ProductBadges";

const nextYear = moment().add(1, 'Y').locale("en_US").format('YYYY-MM-DD');
let imagePrefix = (process.env.REACT_APP_ENABLE_CLOUDFLARE_IMAGE_RESIZE === "true") ? "/cdn-cgi/image/width=200,quality=90/": "";
let imagePrefix2 = imagePrefix ? "/cdn-cgi/image/width=2000,quality=95/": "";
const withCompare = process.env.REACT_APP_WITH_COMPARE === "true";

// todo: too long, split to multiple components
function Product()
{
	const dispatch = useDispatch();
	const user = useSelector(state => state.portal.user);
	const categories = useSelector(state => state.portal.categories);
	const compare = useSelector(state => state.commerce.compare);
	const [product, setProduct] = useState(null);
	const [reviews, setReviews] = useState({});
	const [faqs, setFaqs] = useState({});
	const [addToCartStatus, setAddToCartStatus] = useState("");
	const [quantity, setQuantity] = useState(1);
	const [newReview, setNewReview] = useState({});
	const [selectedAvailabilityDate, setSelectedAvailabilityDate] = useState(null);
	const [selectedAvailability, setSelectedAvailability] = useState(null);
	const fullscreenRef = React.useRef(null);
	const navigate = useNavigate();
	let {store, slug} = useParams();
	const id = slug.split("-").pop();

	useEffect(() =>
	{
		// load product
		api("/commerce/products/"+id, {
			result: (data) => {
				const set = data.attributes_set || {};

				// display setting
				try {
					data.displaySetting = JSON.parse(set.display_setting || "[]");
				} catch (e){console.log(e)}

				if(!data.displaySetting || !data.displaySetting.length) {
					data.displaySetting = [{title: t("description"), attributes_ids: set.attributes?.map(a => a.key)}];
				}

				// first category and it's parents for breadcrumbs
				const firstCategory = (data.categories || [{}])[0];
				data.categoriesBreadcrumbs = [];
				if(firstCategory) {
					// flat the categories menu data to get parents from it
					let categoriesMap = Object.values(categories).flat().reduce((acc, cat) => {
						acc[cat.id] = cat;
						return acc;
					}, {});

					let current = firstCategory;
					while(current) {
						data.categoriesBreadcrumbs.push(current);
						current = categoriesMap[current.parent_id];
					}

					data.categoriesBreadcrumbs.reverse();
				}

				// reviews summary
				let reviewsSummary = {total: 0};
				(data.reviews_summary || []).forEach(review => {
					reviewsSummary[review.rate] = review.count;
					reviewsSummary.total += review.count;
				});
				data.reviews_summary = reviewsSummary;

				// attributes labels
				data.attributesSetting = {};
				(set.attributes || []).forEach(attr => {
					data.attributesSetting[attr.key] = attr;
					if(attr.type === "seats_map") {
						data.seatsMap = data[attr.key];
					}
				});

				// availabilities
				if(data.availabilities)
				{
					let dates = {};

					data.availabilities.forEach(availability => {
						let current = new Date(availability.available_from);
						const end = new Date(availability.available_to);

						while (current <= end) {
							const key = current.toISOString().split("T")[0];
							if(!dates[key]) {
								dates[key] = [];
							}

							dates[key].push(availability);

							current.setDate(current.getDate() + 1);
						}
					});

					data.availabilities = dates;
					data.availabilitiesDates = Object.keys(dates).map(d => new Date(d)) || [];
				}

				setProduct(data);

				// load faqs
				api("/commerce/products-faqs/?product_id="+id+"&category_id="+data.categories.map(c => c.id).join(","), {
					result: (data) => setFaqs(data)
				});
			},
			error: (error) => console.log(error),
		});

		// load reviews
		api("/commerce/reviews/?product_id="+id, {
			result: (data) => setReviews(data)
		});
	}, [id]);

	// add to cart
	const onAddToCart = () =>
	{
		setAddToCartStatus("loading");

		let item = {product_id: product.id, quantity, options: Object.values(product.selectedOptions || {})};
		if(selectedAvailability) {
			item.reserved_from = selectedAvailability.available_from;
			item.reserved_to = selectedAvailability.available_to;
		}

		if(!user) {
			item.productData = product;
		}

		dispatch(addToCart([item], (status, error) => {
			if(error) {
				status = {error};
			}
			setAddToCartStatus(status);
		}, !user));
	};

	// send review
	const sendReview = () => {
		api("/commerce/reviews/", {
			method: "POST",
			data: {...newReview, product_id: id},
			result: () => setNewReview({rate: newReview.rate, success: true}),
			error: (error) => {
				if(error === "You did it before") {
					error = t("You did review it before");
				}

				setNewReview({...newReview, error});
			}
		});
	};

	// addToCartLabel
	let addToCartLabel = "Add to Cart";
	if(addToCartStatus === "loading")
		addToCartLabel = "جاري الإضافة";
	else if(addToCartStatus === "success")
		addToCartLabel = "تم الإضافة";

	addToCartLabel = t(addToCartLabel);
	const shareURL = window.portalSetting("site_url") + window.location.pathname;
	const ratingAverage = product?.stars && product?.reviewers ? (product.stars / product.reviewers).toFixed(1) : 5;

	// compare
	const compareIdx = compare.data.indexOf(product?.id);
	const inCompare = compareIdx !== -1;
	const toggleCompare = () =>
	{
		let newList = [...compare.data];
		if(inCompare) {
			newList.splice(compareIdx, 1);
		} else {
			newList.push(product.id);
		}

		dispatch(addToCompare(newList.slice(-4)))
	};

	/*const modelViewer = function(url)
	{
		// add script
		let lib = "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js";
		if(document.querySelectorAll('[src="' + lib + '"]').length === 0) {
			let script = document.createElement('script');
			script.setAttribute('src', lib);
			script.setAttribute('type', "module");
			document.getElementsByTagName('head')[0].appendChild(script);
		}

		// show the model
		return (
			<div dangerouslySetInnerHTML={{__html: `
				<model-viewer id="AR-product-slider" loading="eager" ar="true" ar-modes="webxr" ar-scale="fixed"
					quick-look-browsers="safari chrome" camera-controls="true"
					src="${url.original}"
					alt=""
					magic-leap="true" unstable-webxr="true" classname="jsx-12754486f2000893"
					style="width: 100%; min-height: 400px;" ar-status="not-presenting">
					<div className="jsx-12754486f2000893 overlay-outline"></div>
				</model-viewer>
				`}}
			/>
		)
	}*/


	return (
		<div id="commerce-product-page">
			{!product && <Spinner animation="border" variant="primary">
				<span className="sr-only">Loading...</span>
			</Spinner>}

			{product &&
			<div className="container">
				<Helmet>
					{/* Primary Meta Tags  */}
					<title>{product.name}</title>
					<meta name="title" content={product.name} />
					<meta name="description" content={product.name} />

					{/* Open Graph / Facebook  */}
					<meta property="og:type" content="website" />
					<meta property="og:url" content={shareURL} />
					<meta property="og:title" content={product.name} />
					<meta property="og:description" content={product.name} />
					<meta property="og:image" content={product.thumbnail} />

					{/* Twitter */}
					<meta property="twitter:card" content="summary_large_image" />
					<meta property="twitter:url" content={shareURL} />
					<meta property="twitter:title" content={product.name} />
					<meta property="twitter:description" content={product.name} />
					<meta property="twitter:image" content={product.thumbnail} />
				</Helmet>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "http://schema.org",
						"@type": "Product",
						"name": product.name,
						"image": product.thumbnail,
						"description": product.name,
						"url": shareURL,
						"brand": {
							"@type": "Brand",
							"name": product.brand?.name,
							"logo": product.brand?.image
						},
						"offers": {
							"@type": "Offer",
							"price": product.current_price,
							"priceCurrency": window.portalSetting("commerce.currency", "", false),
							"priceValidUntil": nextYear,
							"availability": "https://schema.org/InStock",
							"itemCondition": "https://schema.org/NewCondition"
						},
						"aggregateRating": {
							"@type": "AggregateRating",
							"ratingValue": ratingAverage,
							"ratingCount": product.reviewers || 1
						}
					})}
				</script>

				<div id="breadcrumbs">
					<i className={"fa fa-caret-"+t("right")} /> <Link to={"/"+store}>{t("Homepage")}</Link>
					{product.categoriesBreadcrumbs.map((c, i) => <Fragment key={i}>
						<span> / </span>
						<Link key={c.id} to={"/"+store+"/browse/"+c.id}>{c.name}</Link>
					</Fragment>)}
					<span> / </span>
					<a> {product.name}</a>
				</div>

				<Card id="details">
					<div id="sections">
						<div style={{width: "100%"}}>
							{/* All details */}
							<div id="details-container" className="row" style={{display: "flex"}}>
								{/* Main details */}
								<div className="row">
									<div className="col-md-6">
										<h1>{product.name}</h1>
										<h5 style={{color: "#aaa"}}>{product.code}</h5>

										{/*{product.seller && <div id="seller">{t("By")} <Link to={"/"+store+"/browse?seller_id="+product.seller_id}>{product.seller.name}</Link></div>}*/}

										<div className="rating">
											{[1, 2, 3, 4, 5].map(r => <span key={r}>{ratingAverage >= r ? <i className="fa fa-star" /> : (ratingAverage > r-1 ? <i className="fa fa-star-half-stroke" /> : <i className="far fa-star" />)}</span>)} ({ratingAverage})  {product.reviewers} {t("Reviews")}
										</div>

										<div className="price-box">
											{product.type === "parent" && <span>{t("Starting From")}</span>}
											{!!product.saved_amount && <div className="old-price">{product.price.toLocaleString()} {window.portalSetting("commerce.currency")}</div>}
											{!!product.current_price && <div className="price">{product.current_price.toLocaleString()} <span>{window.portalSetting("commerce.currency")}</span></div>}
											{!product.current_price && <div className="price"><span>{t("Free")}</span></div>}
											{!!product.saved_amount && <div className="saved-amount">
												<div className="saved">{t("Discount")} {product.saved_percent}٪ </div>
												{t("saved_amount") + " " + product.saved_amount.toLocaleString()} {window.portalSetting("commerce.currency")}
												{/*<Countdown time={product.special_to} />*/}
											</div>}
											{/*{product.current_price !== 0 && <div className="note">{t("VAT Included")}</div>}*/}
										</div>

										{product.type !== "parent" &&
										<div className={"add-to-cart " + (product.availabilities ? "with-availability" : "")}>
											{product.options &&
											<div id="options">
												{product.options.map(option => {
													const price = option.price - option.discount;
													return (
														<label key={option.id} className="option" style={{display: "flex", alignItems: "center", padding: 5}}>
															<span>{option.name}</span>

															<input
																type="checkbox"
																value={product.selectedOptions?.[option.id] || false}
																onChange={e => {
																	let newProduct = {...product};
																	if(e.target.checked) {
																		newProduct.selectedOptions = {...product.selectedOptions, [option.id]: {id: option.id, key: option.key, value: "yes"}};
																	} else {
																		delete newProduct.selectedOptions[option.id];
																	}

																	setProduct(newProduct);
																}}
															/>
															{!!parseFloat(option.discount) && <span style={{textDecoration: "line-through", margin: "0 5px"}}>{option.price} {window.portalSetting("commerce.currency")}</span>}
															<span>{price ? price + " " + window.portalSetting("commerce.currency") : t("Free")}</span>
														</label>
													)
												})}
											</div>
											}

											{product.availabilities &&
											<div id="availabilities">
												<h6>{t("Appointment Booking")}</h6>

												<Datepicker
													inline
													includeDates={product.availabilitiesDates}
													selected={selectedAvailabilityDate}
													onChange={(date, e) => setSelectedAvailabilityDate(date)}
												/>

												{(!product.availabilities || selectedAvailabilityDate) &&
													(product.availabilities[selectedAvailabilityDate.toISOString().split("T")[0]] || []).map(availability =>
														<button key={availability.id} onClick={() => setSelectedAvailability(availability)} className={(selectedAvailability && availability.id === selectedAvailability.id) ? "selected" : ""}>
															{(new Date(availability.available_from)).toLocaleTimeString()}
														</button>
													)}
											</div>}

											{(!product.availabilities || selectedAvailability) &&
											<div style={{display: "flex"}}>
												{product.available_stock ?
												<>
													{addToCartStatus && addToCartStatus.error && <Alert variant="danger">{addToCartStatus.error}</Alert>}
													{!product.availabilities &&	<FormControl type="number" min="1" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value))} />}
													<Button
														onClick={onAddToCart}
														variant={addToCartStatus || "primary"}
														disabled={addToCartStatus === "loading"}
													>
														<i className="fa fa-cart-shopping" /> {addToCartLabel}
													</Button>
												</> : <Button variant="danger" disabled>{t("Out of stock")}</Button>}

												{withCompare &&
												<Button className="add-to-compare" variant={inCompare ? "success" : "info"} onClick={toggleCompare}>
													<i className="fa fa-check-double" /> <div>{t("Compare")}</div>
												</Button>}
											</div>}
										</div>}

										<h6 className="details-text" style={{margin: 0}}>{window.portalSetting("commerce.product_details_text_"+t.getLocale())}</h6>

										<PaymentHooks product={product} />
										{product.gifts && <ProductsCarousel id="gifts" title={t("Gift")} cardsNumber={3} loadedProducts={product.gifts} />}
										{product.brand && <Link className="brand" to={`/${store}/browse?brand_id[]=`+product.brand.id}><img src={product.brand.image} alt={product.brand.name} height="100" /></Link>}
									</div>

									<div id="media" className="col-md-6">
										<ImageGallery
											plugins={[Inline, Thumbnails, Fullscreen, Video]}
											inline={{ style: { aspectRatio: "1 " } }}
											fullscreen={{ ref: fullscreenRef }}
											on={{click: () => fullscreenRef.current?.enter()}}
											slides={(product.media || []).map(m => {
												let slide = {src: imagePrefix2+m.link};
												if(m.type === "video") {
													slide = {type: m.type, poster: imagePrefix+product.thumbnail, sources: [{type: "video/mp4", src: m.link}]}
												}

												return slide;
											})}
										/>

										{/* fixme: remove old gallery: <div id="items">
											<ImageGallery
												thumbnailPosition="right" showPlayButton={false} showNav={false}
												useBrowserFullscreen={false} showFullscreenButton={false}
												isRTL
												items={(product.media || []).map(m => {
													let slide = {
														original: m.link,
														thumbnail: imagePrefix2+m.link,
														renderItem: url => <SideBySideMagnifier alwaysInPlace imageSrc={imagePrefix2+url.original} />
													};

													if(m.type === "video") {
														slide.renderItem = (url) => <video src={url.original} width="100%" controls />;
														slide.renderThumbInner = () => (
															<>
																<img src={imagePrefix+product.thumbnail} alt=""/>
																<i className="fa fa-play" />
															</>
														)
													}
													else if(m.type === "model") {
														slide.renderItem = modelViewer;
														slide.renderThumbInner = () => (
															<>
																<img src={imagePrefix+product.thumbnail} alt=""/>
																<img className="model-360" src="/360.svg" alt=""/>
															</>
														)
													}

													return slide;
												})}
											/>
										</div>*/}

										<div className="extra">
											<div className="share">
												<a href={`https://www.facebook.com/sharer.php?u=${shareURL}`} target="_blank" className="fab fa-facebook-square" rel="noopener noreferrer"> </a>
												<a href={`https://twitter.com/share?url=${shareURL}&text=${product.name}`} target="_blank" className="fab fa-twitter-square" rel="noopener noreferrer"> </a>
												<a href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareURL}`} target="_blank" className="fab fa-linkedin" rel="noopener noreferrer"> </a>
												<a href={`mailto:?Subject=${product.name}&Body=${product.name} ${shareURL}`} target="_blank" className="fa fa-envelope-square" rel="noopener noreferrer"> </a>
											</div>

											{window.platform !== "web" &&
												<button onClick={() => {
													Plugins.Share.share({
														url: shareURL,
														title: product.name,
														text: product.name,
														dialogTitle: product.name,
													});
												}}>
													<i className="fa fa-share" />
												</button>}
										</div>

										<ProductBadges product={product} />
									</div>
								</div>

								<div id="attributes-bar">
									{Object.entries(product.attributesSetting).map(([key, {label, image}]) =>
										product[key] ? <div key={key} className={"attribute "+(image ? "has_image" : "")}>
											{image && <img src={image} alt={label} />}
											<div className="label">{label}</div>
											<div className="value">{product[key]}</div>
										</div> : null
									)}
								</div>

								<div id="tabs">
									<Tabs>
										<Tab eventKey="description" title={t("description")}>
											<div className="description" dangerouslySetInnerHTML={{__html: product.description}} />
										</Tab>

										{(product.displaySetting || []).map((section, i) =>
											<Tab key={i} eventKey={i} title={section.title || "-"} tabClassName="attrs_btn" tabAttrs={{className: "attrs_tab"}}>
												<div className="attributes">
													<div className="section-description">{section.description}</div>

													<h6>{section.title}</h6>
													{(section.attributes_ids || []).map((attribute, j) =>
														product[attribute] ? <div key={j} className={"row "+ attribute}>
															<div className="col-4 label">
																<i className={"fa fa-caret-"+t("right")}></i>
																{(product.attributesSetting[attribute] || {}).label || attribute}
															</div>
															<div className="col-8 value">{product[attribute]}</div>
														</div> : null
													)}
												</div>
											</Tab>
										)}

										<Tab eventKey="reviews" title={t("Reviews")}>
											{(reviews.data || []).map(review =>
												<div className="review" key={review.id}>
													<div className="date">{moment().from(review.created_at)}</div>
													<h6>{review.user?.full_name || review.user?.first_name}</h6>
													<div className="rating">
														{[1, 2, 3, 4, 5].map(r => <span key={r}>{r > review.rate ? "☆" : "★"}</span>)}
													</div>
													<p>{review.comment}</p>
												</div>
											)}

											{(reviews.data || []).length === 0 && <h5 style={{padding: 10, color: "#aaaaaa"}}>{t("There are no reviews right now")}</h5>}
										</Tab>

										<Tab eventKey="faq" title={t("F & Q")}>
											{(faqs.data || []).map((faq, i) =>
												<div className="faq" key={i}>
													<h6>{faq.question}</h6>
													<div dangerouslySetInnerHTML={{__html: faq.answer}} />
												</div>
											)}

											{(faqs.data || []).length === 0 && <h5 style={{padding: 10, color: "#aaaaaa"}}>{t("There are no F&Q right now")}</h5>}
										</Tab>
									</Tabs>
								</div>
							</div>
						</div>


						{/* Sub products */}
						{product.sub_products &&
						<div id="sub-products">
							<h4>Choose Your Ticket</h4>
							{(product.sub_products || []).map(subProduct => {
								const seated = (subProduct.options || []).find(option => option.type === "seat");

								return <ProductBox
									key={subProduct.id}
									product={subProduct}
									className="product-row"
									withAddToCart={!seated}
									withLink={false}
									withRating={false}
									withDescription
									withQuantity
								/>;
							})}

							{!user && <Link className="btn" to={`/${store}/auth/login?from=/${store}/details/${product.slug}-${product.id}`}>Login to Select your seats</Link>}

							{user && product.seatsMap &&
							<Button as={Link} to={`/${store}/seats/${product.seatsMap}/${product.id}`}>
								<i className="fa fa-couch" /> Select Your Seats
							</Button>}
						</div>}
					</div>
				</Card>

				<div id="reviews-summery">
					<div id="summery">
						<h3>{t("Reviews Summary")}</h3>

						{[5, 4, 3, 2, 1].map(rate =>
							<div className="line" key={rate}>
								<div>★</div>
								<div>{rate}</div>
								<div className="progress">
									<div style={{width: ((product.reviews_summary[rate] / product.reviews_summary.total) * 100)+"%"}} />
								</div>
								<div>{product.reviews_summary[rate] || 0}</div>
							</div>
						)}
					</div>

					<div id="add-review">
						<h3>{t("Share your review")}</h3>
						{t("Share your buying experience")}

						{newReview.success && <Alert variant="success">تم إضافة التقييم بنجاح</Alert>}

						<div className="rating">
							{[5, 4, 3, 2, 1].map(r =>
								<span
									key={r}
									className={"fas fa-star "+(newReview.rate >= r ? "active" : "")}
									onClick={() => {
										if(!user) {
											navigate(`/${store}/auth/login?from=/${store}/details/${product.slug}-${product.id}`);
											return;
										}

										if(!newReview.success) {
											setNewReview({...newReview, rate: r});
										}
									}}
								/>
							)}
						</div>

						{newReview.rate && !newReview.success &&
						<div>
							{newReview.error && <Alert variant="danger">{newReview.error}</Alert>}
							<textarea value={newReview.comment} onChange={(e) => setNewReview({...newReview,comment: e.target.value})}/>
							<Button onClick={() => sendReview()}>مشاركة</Button>
						</div>}
					</div>
				</div>

				<ProductsCarousel title={t("Users Also Viewed")} cardsNumber={4} filters={{order_by: "views"}} />
				<ProductsCarousel title={t("You May Also Like")} cardsNumber={4} />
			</div>}
		</div>
	)
}

export default Product;
